import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTracker } from "@/hooks/useBuyerTracker";

export interface CimDoc {
  id: string;
  name: string;
  fileName: string;
  fileSize: number | null;
  /** false = row predates real uploads (bytes never stored) — not attachable */
  ok: boolean;
}

export interface CimEmailDraft {
  subject: string;
  body: string;
  documentIds: string[];
  /** Cc recipients baked into the package (send dialog can add more) */
  cc?: string[];
}

export interface CimPackageResponse {
  package: { emails: CimEmailDraft[]; updatedAt: string } | null;
  docs: CimDoc[];
}

export function useCimPackage(clientId: string, enabled = true) {
  return useQuery<CimPackageResponse>({
    queryKey: ["cim-package", clientId],
    queryFn: () => apiRequest("GET", `/api/companies/${clientId}/cim-package`).then((r) => r.json()),
    enabled: !!clientId && enabled,
  });
}

// Raw fetch (not apiRequest): these endpoints return meaningful error JSON and,
// for send, per-email results on 502 — apiRequest throws before we can read them.
const jsonFetch = async (method: string, url: string, body: unknown) => {
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  return { res, data };
};

export function useSaveCimPackage(clientId: string) {
  return useMutation({
    mutationFn: async (emails: CimEmailDraft[]) => {
      const { res, data } = await jsonFetch("PUT", `/api/companies/${clientId}/cim-package`, { emails });
      if (!res.ok) throw new Error(data?.error || `Failed to save CIM package (HTTP ${res.status})`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cim-package", clientId] }),
  });
}

export interface SendCimBody {
  linkId: string;
  to: { name?: string; address: string }[];
  cc?: string[];
  emailIndexes?: number[];
  completesPackage?: boolean;
  /** Backfill earlier milestones (teaser/nda) for buyers added straight to CIM */
  alsoMarkMilestones?: ("teaser" | "nda")[];
}

export interface SendCimResult {
  results: { index: number; sent: boolean; error?: string }[];
  milestoneMarked: boolean;
}

export function useSendCim(clientId: string) {
  return useMutation<SendCimResult, Error, SendCimBody>({
    mutationFn: async (body) => {
      const { res, data } = await jsonFetch("POST", `/api/companies/${clientId}/cim-package/send`, body);
      // 502 = partial send — surface per-email results rather than throwing
      if (!res.ok && !(res.status === 502 && data?.results)) {
        throw new Error(data?.error || `Send failed (HTTP ${res.status})`);
      }
      return data as SendCimResult;
    },
    onSuccess: (data) => {
      if (data.milestoneMarked) invalidateTracker(clientId);
    },
  });
}

export function useTestSendCim(clientId: string) {
  return useMutation<{ sentTo: string }, Error, void>({
    mutationFn: async () => {
      const { res, data } = await jsonFetch("POST", `/api/companies/${clientId}/cim-package/test-send`, {});
      if (!res.ok) {
        const firstError = data?.results?.find((r: { error?: string }) => r.error)?.error;
        throw new Error(data?.error || firstError || `Test send failed (HTTP ${res.status})`);
      }
      return data;
    },
  });
}

export interface BuyerContact {
  id: string;
  name: string;
  email: string | null;
  title: string | null;
  isPrimary: boolean | null;
}

/** Quick-add a contact to the buyer from the send dialog (no orphan contacts). */
export function useAddBuyerContact(buyerId: string | null) {
  return useMutation<BuyerContact, Error, { name: string; email: string }>({
    mutationFn: async ({ name, email }) => {
      const { res, data } = await jsonFetch("POST", "/api/contacts", { companyId: buyerId, name, email });
      if (!res.ok) throw new Error(data?.error || "Failed to add contact");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contacts", buyerId] }),
  });
}

export function useBuyerContacts(buyerId: string | null) {
  return useQuery<BuyerContact[]>({
    queryKey: ["contacts", buyerId],
    queryFn: () => apiRequest("GET", `/api/contacts?companyId=${buyerId}`).then((r) => r.json()),
    enabled: !!buyerId,
  });
}

export const formatBytes = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

// Raw attachment bytes. Email encoding inflates ~37%: 25 MB raw ≈ 34 MB wire
// (under Exchange's 35 MB send cap); 18 MB raw ≈ 24.7 MB (under common ~25 MB
// receive caps at Gmail/corporate recipients).
export const WARN_EMAIL_BYTES = 18 * 1024 * 1024;
export const HARD_EMAIL_BYTES = 25 * 1024 * 1024;

/** Server flags one-time Microsoft consent errors by pointing at connect=1 */
export const isConsentError = (message: string) => message.includes("connect=1");
export const openConsentFlow = () => { window.location.href = "/auth/login?connect=1"; };
